/* eslint-disable */
import React, { useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { Search, Download, FileText, Loader2, User } from "lucide-react";
import axiosInstance from "../../services/Axios/axios";
import RenderAdminPage from "../RenderAdminPage";
import { LOGO_URL, ORG } from "../../helpers/brand";
import {
  downloadFundStatement,
  shortDate,
  isoDate,
  describeTransaction,
  sortRowsNewestFirst,
} from "../../helpers/fundStatementPdf";

// The funds list endpoint is paginated; a member's whole contribution history
// is well under this, so one page is the whole statement.
const ALL_ROWS = 2000;

const cedi = (n) =>
  Number(n || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

function MemberStatement() {
  const [staffId, setStaffId] = useState("");
  const [searching, setSearching] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [member, setMember] = useState(null);
  const [rows, setRows] = useState(null); // null = no search yet, [] = searched, nothing found

  const total = useMemo(
    () => (rows || []).reduce((sum, r) => sum + Number(r.deduction || 0), 0),
    [rows]
  );
  const ordered = useMemo(() => sortRowsNewestFirst(rows || []), [rows]);

  const search = async (e) => {
    e?.preventDefault();
    const id = staffId.trim();
    if (!id) return toast.info("Enter a staff ID to search");

    setSearching(true);
    setMember(null);
    setRows(null);
    try {
      // The member record carries the header details (facility, district,
      // region); the fund rows carry the transactions. Neither call fails the
      // other — a member with no fund rows is still a valid search result.
      const [memberRes, fundsRes] = await Promise.allSettled([
        axiosInstance.get(`/users/members/${encodeURIComponent(id)}`),
        axiosInstance.get(
          `/funds?isPaginated=true&page=1&size=${ALL_ROWS}&search=${encodeURIComponent(id)}`
        ),
      ]);

      // The /funds search is a prefix regex (^id), so "9019" also returns
      // 901925, 901926… Keep only this member's rows or the statement totals
      // would mix members together.
      const page = fundsRes.status === "fulfilled" ? fundsRes.value?.data?.data : null;
      const returned = page?.docs ?? [];
      const fundRows = returned.filter((r) => String(r.staff_id) === id);

      // One page is the whole history for any real member, but if the prefix
      // search ever fills a page there may be rows for this member we never
      // saw — say so rather than printing a short statement that looks whole.
      if (returned.length >= ALL_ROWS && Number(page?.totalItems) > returned.length) {
        toast.warn(
          `Only the first ${returned.length} of ${page.totalItems} records matching "${id}" were loaded — this statement may be incomplete.`
        );
      }

      // Fall back to the member snapshot embedded in a fund row when the
      // member lookup itself came back empty.
      const memberDoc =
        (memberRes.status === "fulfilled" && memberRes.value?.data) ||
        fundRows.find((r) => r?.user?.name)?.user ||
        null;

      if (!memberDoc && !fundRows.length) {
        toast.error(`No member or contributions found for ${id}`);
        setRows([]);
        return;
      }

      setMember({ staff_id: id, ...(memberDoc || {}) });
      setRows(fundRows);
      if (!fundRows.length) toast.info("Member found, but no contributions recorded");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Search failed. Try again.");
      setRows([]);
    } finally {
      setSearching(false);
    }
  };

  const download = async () => {
    if (!member || !ordered.length) return toast.info("Nothing to download");
    setDownloading(true);
    try {
      await downloadFundStatement({ member, rows: ordered });
    } catch (err) {
      console.error(err);
      toast.error("Could not build the statement PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <RenderAdminPage title="Member Statement">
      <p className="text-sm text-slate-500 dark:text-slate-400 -mt-2 mb-6">
        Look up a member by staff ID to review their fund contributions and download a
        watermarked statement.
      </p>

      {/* Search */}
      <form
        onSubmit={search}
        className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm p-5 mb-6"
      >
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <label
              htmlFor="staffId"
              className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              Staff ID
            </label>
            <div className="relative">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                id="staffId"
                type="search"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                placeholder="e.g. 901925"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-slate-800 dark:text-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all placeholder:text-slate-400"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={searching}
            className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {searching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {searching ? "Searching…" : "Search"}
          </button>
        </div>
      </form>

      {/* Member header + statement */}
      {member && (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm p-5 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                  {member.profile_image ? (
                    <img
                      src={member.profile_image}
                      alt={member.name}
                      className="w-12 h-12 rounded-xl object-cover"
                    />
                  ) : (
                    <User size={22} />
                  )}
                </div>
                <div>
                  <p className="text-base font-bold text-slate-800 dark:text-slate-100">
                    {member.name || "Unknown member"}
                  </p>
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                    {member.staff_id}
                  </p>
                  <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-1 text-xs">
                    {[
                      ["Facility", member.facility],
                      ["Department", member.department],
                      ["District", member.district],
                      ["Region", member.region],
                      ["Phone", member.phone_number],
                      ["Statement as at", isoDate()],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <dt className="text-slate-400 dark:text-slate-500">{k}</dt>
                        <dd className="text-slate-700 dark:text-slate-300 font-medium">
                          {v || "—"}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>

              <div className="flex flex-col items-start sm:items-end gap-3">
                <div className="text-right">
                  <p className="text-xs text-slate-400 dark:text-slate-500">Total contributions</p>
                  <p className="text-2xl font-black text-indigo-600 dark:text-indigo-400">
                    ¢{cedi(total)}
                  </p>
                  <p className="text-xs text-slate-400">{ordered.length} entries</p>
                </div>
                <button
                  onClick={download}
                  disabled={downloading || !ordered.length}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {downloading ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <Download size={15} />
                  )}
                  {downloading ? "Preparing…" : "Download Statement"}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-gray-700">
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                Fund Contributions
              </h3>
            </div>
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0">
                  <tr className="bg-slate-50 dark:bg-gray-700/50">
                    {["Date", "Description", "Reference", "Period"].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Deduction
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                  {ordered.length ? (
                    ordered.map((r, i) => (
                      <tr
                        key={(r._id || r.period) + i}
                        className="hover:bg-slate-50 dark:hover:bg-gray-700/30 transition-colors"
                      >
                        <td className="px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300">
                          {shortDate(r.date)}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300">
                          {describeTransaction(r.transaction_type)}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400">
                          {r.reference || r.period || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400">
                          {r.period || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-right font-semibold text-slate-700 dark:text-slate-300">
                          ¢{cedi(r.deduction)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-4 py-12 text-center text-sm text-slate-400 dark:text-slate-500"
                      >
                        No contributions recorded for this member
                      </td>
                    </tr>
                  )}
                </tbody>
                {ordered.length > 0 && (
                  <tfoot>
                    <tr className="bg-slate-50 dark:bg-gray-700/50 border-t-2 border-slate-200 dark:border-gray-600">
                      <td
                        colSpan="4"
                        className="px-4 py-3 text-sm font-bold text-slate-800 dark:text-slate-100"
                      >
                        TOTAL
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-black text-indigo-600 dark:text-indigo-400">
                        ¢{cedi(total)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}

      {!member && !searching && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-slate-200 dark:border-gray-700 p-12 text-center">
          <img
            src={LOGO_URL}
            alt=""
            className="h-16 w-auto mx-auto mb-4 opacity-40"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <FileText size={28} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Search a staff ID to load that member&rsquo;s {ORG.name.join(" ")} contributions.
          </p>
        </div>
      )}

      <ToastContainer autoClose={3000} />
    </RenderAdminPage>
  );
}

export default MemberStatement;
